<template>
  <div :class="embedded ? '' : 'min-h-screen bg-gray-50 dark:bg-gray-900 p-6'">
    <div v-if="!embedded" class="flex flex-col gap-4 mb-6 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">租户团队与租户用户</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          为租户创建初始 owner 账号，并把 Home 用户绑定到该租户作用域中。
        </p>
      </div>

      <div class="w-full max-w-sm">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">选择租户</label>
        <select
          v-model.number="selectedTenantId"
          class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 text-gray-900 dark:text-gray-100"
        >
          <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
            {{ tenant.name }}
          </option>
        </select>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-[1.05fr_0.95fr]">
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div class="flex items-center justify-between gap-3 mb-5">
          <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">租户团队账号</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">建议先为租户创建一个 owner 账号，后续由租户自己在 Tenant Console 管理团队成员。</p>
          </div>
          <span class="text-xs px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
            Tenant Workspace Setup
          </span>
        </div>

        <form class="grid grid-cols-1 gap-4 md:grid-cols-2" @submit.prevent="handleCreateAccount">
          <label class="md:col-span-2">
            <span class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">展示名</span>
            <input v-model.trim="accountForm.displayName" type="text" class="form-input" placeholder="例如：租户演示管理员" />
          </label>

          <label>
            <span class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">登录名</span>
            <input v-model.trim="accountForm.loginName" type="text" class="form-input" placeholder="例如：demo_admin" />
          </label>

          <label>
            <span class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">密码</span>
            <input v-model="accountForm.password" type="text" class="form-input" placeholder="至少 8 位" />
          </label>

          <label>
            <span class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">账号角色</span>
            <select v-model="accountForm.role" class="form-input">
              <option value="owner">租户所有者</option>
              <option value="operator">租户运营者</option>
            </select>
          </label>

          <div class="md:col-span-2 flex items-center justify-end">
            <button class="primary-btn min-w-[150px] justify-center" type="submit" :disabled="isCreatingAccount">
              {{ isCreatingAccount ? '创建中...' : '创建控制台账号' }}
            </button>
          </div>
        </form>

        <div class="mt-6 overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead class="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th class="table-th">登录名</th>
                <th class="table-th">展示名</th>
                <th class="table-th">角色</th>
                <th class="table-th">状态</th>
                <th class="table-th">最近登录</th>
                <th class="table-th text-right">操作</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr v-for="account in accounts" :key="account.id" class="hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors">
                <td class="table-td font-medium">{{ account.loginName }}</td>
                <td class="table-td">{{ account.displayName }}</td>
                <td class="table-td">
                  <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium" :class="account.role === 'owner'
                    ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'">
                    {{ TENANT_CONSOLE_ACCOUNT_ROLE_LABELS[account.role] }}
                  </span>
                </td>
                <td class="table-td">
                  <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium" :class="account.status === 'active'
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'">
                    {{ TENANT_CONSOLE_ACCOUNT_STATUS_LABELS[account.status] }}
                  </span>
                </td>
                <td class="table-td">{{ account.lastLoginAt ? formatDate(account.lastLoginAt) : '尚未登录' }}</td>
                <td class="table-td text-right">
                  <div class="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      class="link-btn-view font-medium"
                      @click="openResetPasswordModal(account)"
                    >
                      重置密码
                    </button>
                    <button
                      class="link-btn-edit font-medium"
                      :disabled="updatingAccountId === account.id"
                      @click="handleToggleAccountStatus(account)"
                    >
                      {{ account.status === 'active' ? '停用' : '启用' }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!accounts.length">
                <td colspan="6" class="table-empty">当前租户还没有租户控制台账号</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div class="flex items-center justify-between gap-3 mb-5">
          <div>
            <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">绑定租户用户</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">从现有 Home 用户里选择，绑定到当前租户的控制台视角。</p>
          </div>
          <button class="secondary-btn" type="button" @click="loadBindings">刷新绑定列表</button>
        </div>

        <div class="space-y-4">
          <div class="flex gap-3">
            <input
              v-model.trim="userSearchQuery"
              type="text"
              class="form-input flex-1"
              placeholder="搜索用户名或手机号"
              @keyup.enter="handleSearchUsers"
            />
            <button class="ghost-btn" type="button" @click="handleSearchUsers">搜索用户</button>
          </div>

          <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th class="table-th">ID</th>
                  <th class="table-th">用户名</th>
                  <th class="table-th">手机号</th>
                  <th class="table-th">角色</th>
                  <th class="table-th text-right">操作</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="user in searchedUsers" :key="user.id">
                  <td class="table-td">{{ user.id }}</td>
                  <td class="table-td">{{ user.username }}</td>
                  <td class="table-td">{{ user.phone || '-' }}</td>
                  <td class="table-td">{{ roleMap[user.role] || '未知角色' }}</td>
                  <td class="table-td text-right">
                    <button class="link-btn-view font-medium" :disabled="bindingUserId === user.id" @click="handleBindUser(user.id)">
                      绑定到租户
                    </button>
                  </td>
                </tr>
                <tr v-if="!searchedUsers.length">
                  <td colspan="5" class="table-empty">先搜索用户，再将其绑定到当前租户</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="border-t border-gray-200 dark:border-gray-700 pt-5">
            <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">当前绑定用户</h3>
            <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-900/40">
                  <tr>
                    <th class="table-th">用户名</th>
                    <th class="table-th">手机号</th>
                    <th class="table-th">角色</th>
                    <th class="table-th">绑定时间</th>
                    <th class="table-th text-right">操作</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <tr v-for="binding in bindings" :key="binding.id">
                    <td class="table-td">{{ binding.username }}</td>
                    <td class="table-td">{{ binding.phone || '-' }}</td>
                    <td class="table-td">{{ roleMap[binding.role] || '未知角色' }}</td>
                    <td class="table-td">{{ formatDate(binding.boundAt) }}</td>
                    <td class="table-td text-right">
                      <button class="link-btn-delete font-medium" :disabled="deletingBindingId === binding.id" @click="handleDeleteBinding(binding.id)">
                        解绑
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!bindings.length">
                    <td colspan="5" class="table-empty">当前租户尚未绑定任何用户</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>

    <Modal :show="showResetPasswordModal" :title="resetPasswordModalTitle" size="small" @close="closeResetPasswordModal">
      <form class="space-y-4" @submit.prevent="handleResetPassword">
        <p class="text-sm leading-6 text-gray-600 dark:text-gray-300">
          将为
          <span class="font-medium text-gray-900 dark:text-gray-100">{{ resetPasswordTarget?.displayName }}</span>
          重新设置登录密码。新密码需至少 8 位。
        </p>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">登录名</label>
          <input
            :value="resetPasswordTarget?.loginName || ''"
            type="text"
            class="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-300"
            disabled
          />
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">新密码</label>
          <input
            v-model="resetPasswordForm.password"
            type="password"
            autocomplete="new-password"
            class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            :disabled="isResettingPassword"
          />
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">确认新密码</label>
          <input
            v-model="resetPasswordForm.confirmPassword"
            type="password"
            autocomplete="new-password"
            class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            :disabled="isResettingPassword"
          />
        </div>

        <div class="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button class="cancel-btn" type="button" :disabled="isResettingPassword" @click="closeResetPasswordModal">取消</button>
          <button class="primary-btn" type="submit" :disabled="isResettingPassword">
            {{ isResettingPassword ? '重置中...' : '确认重置密码' }}
          </button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";

import { searchUsersAPI } from "@/api/user";
import { getTenantsAPI } from "@/api/admin/tenants";
import {
  createTenantConsoleAccountAPI,
  createTenantUserBindingAPI,
  deleteTenantUserBindingAPI,
  getTenantConsoleAccountsAPI,
  getTenantUserBindingsAPI,
  resetTenantConsoleAccountPasswordAPI,
  updateTenantConsoleAccountStatusAPI,
} from "@/api/admin/tenantConsole";
import Modal from "@/components/common/Modal.vue";
import type { AdminTenantConfig } from "@/types/admin/tenant";
import {
  TENANT_CONSOLE_ACCOUNT_ROLE_LABELS,
  TENANT_CONSOLE_ACCOUNT_STATUS_LABELS,
  type TenantConsoleAccount,
  type TenantConsoleAccountRole,
  type TenantUserBinding,
} from "@/types/admin/tenantConsole";
import { UserRole } from "@/types/auth/role";
import type { User } from "@/types/user/user";
import toast from "@/utils/toaster";

const props = withDefaults(defineProps<{
  tenantId?: number;
  tenantName?: string;
  embedded?: boolean;
}>(), {
  tenantId: undefined,
  tenantName: "",
  embedded: false,
});

const tenants = ref<AdminTenantConfig[]>([]);
const selectedTenantId = ref<number>(0);

const accounts = ref<TenantConsoleAccount[]>([]);
const bindings = ref<TenantUserBinding[]>([]);
const searchedUsers = ref<User[]>([]);

const userSearchQuery = ref("");
const isCreatingAccount = ref(false);
const updatingAccountId = ref<number | null>(null);
const bindingUserId = ref<number | null>(null);
const deletingBindingId = ref<number | null>(null);
const isResettingPassword = ref(false);
const showResetPasswordModal = ref(false);
const resetPasswordTarget = ref<TenantConsoleAccount | null>(null);

const accountForm = reactive<{
  displayName: string;
  loginName: string;
  password: string;
  role: TenantConsoleAccountRole;
}>({
  displayName: "",
  loginName: "",
  password: "",
  role: "owner",
});

const resetPasswordForm = reactive({
  password: "",
  confirmPassword: "",
});

const resetPasswordModalTitle = computed(() => {
  if (!resetPasswordTarget.value) {
    return "重置密码";
  }
  return `重置密码 · ${resetPasswordTarget.value.displayName}`;
});

const roleMap: Record<number, string> = {
  [UserRole.USER]: "普通用户",
  [UserRole.SUPER_ADMIN]: "超级管理员",
  [UserRole.ADMIN]: "管理员",
  [UserRole.TEACHER]: "教师",
};

const formatDate = (value: string) => new Date(value).toLocaleString("zh-CN");

/**
 * 加载租户列表。
 */
const loadTenants = async () => {
  if (props.embedded && props.tenantId) {
    tenants.value = props.tenantName ? [{ id: props.tenantId, name: props.tenantName } as AdminTenantConfig] : [];
    selectedTenantId.value = props.tenantId;
    return;
  }

  const response = await getTenantsAPI({ page: 1, limit: 100 });
  if (response.status !== 200 || !response.data) {
    toast.error(response.message || "加载租户列表失败");
    return;
  }

  tenants.value = response.data.tenants;
  if (!selectedTenantId.value && tenants.value.length > 0) {
    selectedTenantId.value = tenants.value[0].id;
  }
};

/**
 * 加载租户控制台账号列表。
 */
const loadAccounts = async () => {
  if (!selectedTenantId.value) {
    accounts.value = [];
    return;
  }

  const response = await getTenantConsoleAccountsAPI({
    tenantId: selectedTenantId.value,
    page: 1,
    limit: 50,
  });
  if (response.status !== 200 || !response.data) {
    toast.error(response.message || "加载租户控制台账号失败");
    return;
  }

  accounts.value = response.data.accounts;
};

/**
 * 加载当前租户的绑定用户。
 */
const loadBindings = async () => {
  if (!selectedTenantId.value) {
    bindings.value = [];
    return;
  }

  const response = await getTenantUserBindingsAPI({
    tenantId: selectedTenantId.value,
    page: 1,
    limit: 100,
  });
  if (response.status !== 200 || !response.data) {
    toast.error(response.message || "加载绑定用户失败");
    return;
  }

  bindings.value = response.data.bindings;
};

/**
 * 创建租户控制台账号。
 */
const handleCreateAccount = async () => {
  if (!selectedTenantId.value) {
    toast.error("请先选择租户");
    return;
  }

  if (!accountForm.displayName || !accountForm.loginName || !accountForm.password) {
    toast.error("请完整填写账号信息");
    return;
  }

  isCreatingAccount.value = true;
  try {
    const response = await createTenantConsoleAccountAPI({
      tenantId: selectedTenantId.value,
      loginName: accountForm.loginName,
      displayName: accountForm.displayName,
      password: accountForm.password,
      role: accountForm.role,
    });

    if (response.status !== 200) {
      toast.error(response.message || "创建租户控制台账号失败");
      return;
    }

    toast.success("租户控制台账号已创建");
    accountForm.displayName = "";
    accountForm.loginName = "";
    accountForm.password = "";
    accountForm.role = "owner";
    await loadAccounts();
  } finally {
    isCreatingAccount.value = false;
  }
};

/**
 * 切换账号状态。
 */
const handleToggleAccountStatus = async (account: TenantConsoleAccount) => {
  updatingAccountId.value = account.id;
  try {
    const nextStatus = account.status === "active" ? "suspended" : "active";
    const response = await updateTenantConsoleAccountStatusAPI(account.id, nextStatus);
    if (response.status !== 200) {
      toast.error(response.message || "更新账号状态失败");
      return;
    }

    toast.success("账号状态已更新");
    await loadAccounts();
  } finally {
    updatingAccountId.value = null;
  }
};

const resetResetPasswordForm = () => {
  resetPasswordForm.password = "";
  resetPasswordForm.confirmPassword = "";
};

const clearResetPasswordState = () => {
  showResetPasswordModal.value = false;
  resetPasswordTarget.value = null;
  resetResetPasswordForm();
};

const closeResetPasswordModal = () => {
  if (isResettingPassword.value) {
    return;
  }

  clearResetPasswordState();
};

const openResetPasswordModal = (account: TenantConsoleAccount) => {
  resetPasswordTarget.value = account;
  resetResetPasswordForm();
  showResetPasswordModal.value = true;
};

/**
 * 重置账号密码。
 */
const handleResetPassword = async () => {
  if (!resetPasswordTarget.value) {
    return;
  }

  if (resetPasswordForm.password.length < 8) {
    toast.error("密码至少需要 8 位");
    return;
  }

  if (resetPasswordForm.password !== resetPasswordForm.confirmPassword) {
    toast.error("两次输入的密码不一致");
    return;
  }

  isResettingPassword.value = true;
  try {
    const response = await resetTenantConsoleAccountPasswordAPI(resetPasswordTarget.value.id, {
      password: resetPasswordForm.password,
    });

    if (response.status !== 200) {
      toast.error(response.message || "重置密码失败");
      return;
    }

    toast.success("重置密码成功");
    clearResetPasswordState();
    await loadAccounts();
  } finally {
    isResettingPassword.value = false;
  }
};

/**
 * 搜索可绑定用户。
 */
const handleSearchUsers = async () => {
  if (!userSearchQuery.value) {
    searchedUsers.value = [];
    return;
  }

  const response = await searchUsersAPI({
    query: userSearchQuery.value,
    status: "all",
    page: 1,
    limit: 20,
  });

  if (response.status !== 200 || !response.data) {
    toast.error(response.message || "搜索用户失败");
    return;
  }

  searchedUsers.value = (response.data.users || []).filter((user) => user.role !== UserRole.ADMIN && user.role !== UserRole.SUPER_ADMIN);
};

/**
 * 把用户绑定到当前租户。
 */
const handleBindUser = async (userId: number) => {
  if (!selectedTenantId.value) {
    toast.error("请先选择租户");
    return;
  }

  bindingUserId.value = userId;
  try {
    const response = await createTenantUserBindingAPI({
      tenantId: selectedTenantId.value,
      userId,
    });

    if (response.status !== 200) {
      toast.error(response.message || "绑定用户失败");
      return;
    }

    toast.success("已绑定到当前租户");
    await loadBindings();
  } finally {
    bindingUserId.value = null;
  }
};

/**
 * 删除用户绑定。
 */
const handleDeleteBinding = async (bindingId: number) => {
  deletingBindingId.value = bindingId;
  try {
    const response = await deleteTenantUserBindingAPI(bindingId);
    if (response.status !== 200) {
      toast.error(response.message || "解绑失败");
      return;
    }

    toast.success("已解绑当前用户");
    await loadBindings();
  } finally {
    deletingBindingId.value = null;
  }
};

watch(selectedTenantId, async () => {
  await Promise.all([loadAccounts(), loadBindings()]);
});

watch(
  () => props.tenantId,
  async (nextTenantId) => {
    if (nextTenantId) {
      selectedTenantId.value = nextTenantId;
      await Promise.all([loadAccounts(), loadBindings()]);
    }
  },
);

onMounted(async () => {
  await loadTenants();
  await Promise.all([loadAccounts(), loadBindings()]);
});
</script>

<style scoped>
.form-input {
  @apply w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 text-gray-900 dark:text-gray-100;
}

.table-th {
  @apply px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400;
}

.table-td {
  @apply px-4 py-3 text-sm text-gray-700 dark:text-gray-200 align-top;
}

.table-empty {
  @apply px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500;
}

.status-tag {
  @apply inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium;
}

.tag-green {
  @apply bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300;
}

.tag-gray {
  @apply bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200;
}

.ghost-btn {
  @apply rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200;
}
</style>
